import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatContext, { useChat } from '../../contexts/ChatContext';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Avatar, Input, Button, Skeleton, Typography, Empty, Badge } from 'antd';
import { SendOutlined, ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const formatMessageTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid time string provided to formatMessageTime:', dateString);
      return '';
    }
    
    return format(date, 'HH:mm');
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

const formatMessageDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided to formatMessageDate:', dateString);
      return '';
    }
    
    if (isToday(date)) {
      return 'Aujourd\'hui';
    } else if (isYesterday(date)) {
      return 'Hier';
    } else {
      return format(date, 'd MMMM yyyy', { locale: fr });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const ChatWindow = ({ conversation, onBack, className = '' }) => {
  const { currentUser, messages, sendMessage, isTyping, typingUserId, loadMessages, currentConversation } = useChat();
  const otherUser = conversation?.otherUser || {};
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Group messages by date with null checks
  const groupedMessages = messages.reduce((groups, msg) => {
    if (!msg) return groups;
    
    // Ensure message has a valid createdAt date
    const messageDate = msg.createdAt || new Date().toISOString();
    const date = formatMessageDate(messageDate);
    
    if (!groups[date]) {
      groups[date] = [];
    }
    
    // Ensure message has all required fields
    const safeMessage = {
      ...msg,
      id: msg.id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: msg.content || '',
      createdAt: messageDate,
      senderId: msg.senderId || 'unknown',
      isRead: msg.isRead !== undefined ? msg.isRead : false
    };
    
    groups[date].push(safeMessage);
    return groups;
  }, {});

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Load more messages when scrolling up
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop } = messagesContainerRef.current;
    if (scrollTop === 0 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
  }, [hasMore, loadingMore, messages.length]);

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const oldestMessage = messages[0];
      const newMessages = await loadMessages(conversation.id, oldestMessage?.id);
      
      if (newMessages.length < 20) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isSending) return;
    
    setIsSending(true);
    try {
      await sendMessage(message);
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    
    // Notify typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Send typing indicator
    sendTyping(true);
    
    // Reset typing indicator after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
    }, 3000);
  };

  const sendTyping = (isTyping) => {
    // This function will be called from the context
  };

  if (!conversation) {
    return (
      <div className={`flex-1 flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4 ${className}`}>
        <Empty 
          description={
            <span className="text-gray-500 dark:text-gray-400">
              Sélectionnez une conversation pour commencer à discuter
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={onBack}
          className="md:hidden mr-2"
        />
        <div className="flex items-center">
          <Badge 
            dot={otherUser?.id && typingUserId === otherUser.id && isTyping}
            status="success"
            offset={[-5, 35]}
          >
            <Avatar size="large" className="bg-blue-500">
              {otherUser?.firstName ? 
                otherUser.firstName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          </Badge>
          <div className="ml-3">
            <div className="font-medium text-gray-900 dark:text-white">
              {otherUser?.companyName || 
               `${otherUser?.firstName || ''} ${otherUser?.lastName || ''}`.trim() || 'Utilisateur'}
              
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {otherUser?.id && typingUserId === otherUser.id && isTyping 
                ? 'en train d\'écrire...' 
                : 'en ligne'}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {loadingMore && (
          <div className="flex justify-center py-2">
            <LoadingOutlined className="text-gray-400" />
          </div>
        )}

        {Object.entries(groupedMessages).length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            Aucun message. Commencez la conversation !
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date} className="space-y-4">
              <div className="text-center my-4">
                <span className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 px-3 py-1 rounded-full">
                  {date}
                </span>
              </div>
              
              {dateMessages.filter(msg => msg).map((msg, index) => {
                const isCurrentUser = currentUser?.id && msg?.senderId === currentUser.id;
                if (!msg) return null;
                return (
                  <div 
                    key={msg.id || index} 
                    className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                  >
                  <div 
                    className={`max-w-xs md:max-w-md lg:max-w-lg xl:max-w-xl rounded-lg px-4 py-2 ${
                      isCurrentUser
                        ? 'bg-blue-500 text-white rounded-br-none' 
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-none'
                    }`}
                  >
                    {msg.content}
                    <div 
                      className={`text-xs mt-1 text-right ${
                        isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {formatMessageTime(msg.createdAt)}
                      {isCurrentUser && (
                        <span className="ml-1">
                          {msg.isRead ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        
        {isTyping && otherUser?.id && typingUserId === otherUser.id && (
          <div className="flex items-center space-x-2">
            <Avatar size="small" className="bg-blue-500">
              {otherUser?.firstName ? 
                otherUser.firstName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-end space-x-2">
          <TextArea
            placeholder="Écrivez votre message..."
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1"
            disabled={isSending}
          />
          <Button
            type="primary"
            icon={isSending ? <LoadingOutlined /> : <SendOutlined />}
            onClick={handleSendMessage}
            loading={isSending}
            className="h-10"
            disabled={!message.trim()}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
